import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import CModal from '@/components/CModal.vue';
import CButton from '@/components/shared/CButton';
import CBadge from '@/components/shared/CBadge';
import CInput from '@/components/shared/CInput';
import type { Tag } from '@/api/typings/tags';

// Must match TagsBL.MaxTagsPerProduct — the real limit is enforced server-side in
// ProductBL.AssignTags, this default only covers the brief window before the caller has the
// server-provided value to pass down via the maxTags prop.
type NewTagOption = { id: string; name: string };
type SelectedTag = Tag | NewTagOption;


export default /*#__PURE__*/_defineComponent({
  __name: 'ManageTagsModal',
  props: {
    value: { type: Boolean },
    productId: { default: '' },
    tags: { default: () => [] },
    availableTags: { default: () => [] },
    maxTags: { default: 5 }
  },
  emits: ["close", "save"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'close'): void;
    (event: 'save', payload: { productId: string; tagIds: number[]; newTagNames: string[] }): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: boolean, productId: string, tags: Tag[], availableTags: Tag[], maxTags: number };



const effectiveMaxTags = computed(() => props.maxTags);



// A selected tag is either a real, already-existing Tag (number id) or a not-yet-created one
// represented by a placeholder whose id IS its name. selectedTagIds is the single source of truth
// for both, so tagIds/newTagNames can always be derived from it at save time instead of tracked
// separately.
const selectedTagIds = ref<Array<number | string>>([]);
const search = ref('');

watch(
    () => props.value,
    (isOpen) => {
        if (isOpen) {
            selectedTagIds.value = props.tags.map((tag) => tag.id);
            search.value = '';
        }
    }
);

const trimmedSearch = computed(() => search.value.trim());
const isMaxReached = computed(() => selectedTagIds.value.length >= effectiveMaxTags.value);

const selectedTags = computed<SelectedTag[]>(() =>
    selectedTagIds.value.map((id) => {
        if (typeof id === 'string') {
            return { id, name: id };
        }
        // Prefer the product's own tag data (always current) over the once-loaded availableTags
        // cache, which can be missing a tag assigned elsewhere (e.g. by another session) and would
        // otherwise fall back to displaying the raw id instead of its name.
        return (
            props.tags.find((tag) => tag.id === id) ??
            props.availableTags.find((tag) => tag.id === id) ?? { id, name: String(id) }
        );
    })
);

// Tags matching the typed text, excluding ones already selected — shown as clickable chips only
// while the user is actively typing (empty search shows nothing, per design).
const filteredTags = computed<Tag[]>(() => {
    const term = trimmedSearch.value.toLowerCase();
    if (!term) {
        return [];
    }
    return props.availableTags.filter(
        (tag) => tag.name.toLowerCase().includes(term) && !selectedTagIds.value.includes(tag.id)
    );
});

// Checked against both real tags AND not-yet-created ones already in the selection, so typing the
// same new tag name twice can't push a second, duplicate placeholder into selectedTagIds.
const hasExactMatch = computed(() => {
    const term = trimmedSearch.value.toLowerCase();
    return (
        props.availableTags.some((tag) => tag.name.toLowerCase() === term) ||
        selectedTagIds.value.some((id) => typeof id === 'string' && id.toLowerCase() === term)
    );
});

const showCreateButton = computed(() => trimmedSearch.value.length > 0 && !hasExactMatch.value && !isMaxReached.value);

const removeTag = (tag: SelectedTag) => {
    selectedTagIds.value = selectedTagIds.value.filter((id) => id !== tag.id);
};

const addToSelection = (id: number | string) => {
    if (isMaxReached.value) {
        return;
    }
    selectedTagIds.value = selectedTagIds.value.concat(id);
    search.value = '';
};

const selectTag = (tag: Tag) => addToSelection(tag.id);

const createTag = () => {
    if (!trimmedSearch.value) {
        return;
    }
    addToSelection(trimmedSearch.value);
};

// Enter mirrors clicking the top suggestion (if any match is showing) or the "Create tag" button,
// so keyboard-only use isn't limited to Tab-ing to each chip button.
const onEnter = () => {
    if (filteredTags.value.length) {
        selectTag(filteredTags.value[0]);
    } else if (showCreateButton.value) {
        createTag();
    }
};

const onClose = () => emit('close');

const onSave = () => {
    const tagIds = selectedTagIds.value.filter((id): id is number => typeof id === 'number');
    const newTagNames = selectedTagIds.value.filter((id): id is string => typeof id === 'string');
    emit('save', { productId: props.productId, tagIds, newTagNames });
};

return { __sfc: true,props, effectiveMaxTags, emit, selectedTagIds, search, trimmedSearch, isMaxReached, selectedTags, filteredTags, hasExactMatch, showCreateButton, removeTag, addToSelection, selectTag, createTag, onEnter, onClose, onSave, CModal, CButton, CBadge, CInput }
}

})